import { Dimension, Kante, Knoten, Polygon, RahmenDef } from "./types";
import { Box2, ExtrudeGeometry, Material, Mesh, Shape, Vector2, Matrix4 } from "three";
import { v2, v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import { Geometry } from "oc-threejs/geometries/Geometry";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths from "oc-commons/Maths";
import { extrudePolygons } from "./extrudePolygons";
import { ThreeBSP } from "oc-threejs/ThreeBSP";

export type RahmenParams = {
  torHöhe: number;
  lichteWeite: number;
  schiebetorFlügelMaxBreite: number;
  dreieckLinks?: boolean;
  gespiegeltZ: boolean;
};

export const gummiPufferBreite = 0.05;
export const bevelSize = 0.001;

export function schiebetorKennzahlen({
  lichteWeite,
  torHöhe,
}: {
  lichteWeite: number;
  torHöhe: number;
}) {
  // die folgenden Formeln stammen aus dem Excel Breitenberechnung Schiebetor 13-11-2023
  const mm = 1 / 1000;
  // Tabelle 1 ------
  const rahmenBreite = 80 * mm;
  const L1 = lichteWeite;
  const L2 = L1 * 0.3;
  const E = 190 * mm;
  const L5 = 200 * mm;
  const L4 = L2 + (E + L5) * 2;
  // Gesamtbreite des Schiebetor-Rahmens
  const LT = L1 + L4;
  // Maximale Breite Fenster
  const L3 = 1.86;

  // Tabelle 2 -----
  const A = LT;
  const C4 = L1;
  const B = torHöhe + 10 * mm;
  const C = C4 - 40 * mm + 185 * mm;
  // knickDeltaX
  const D = A - C;
  // Dreieckswinkel
  const F1 = (torHöhe - 130 * mm) / D;
  const F = Math.atan(F1);
  const abstandKnickObenErsteStrebe = 185 * mm;
  const G = abstandKnickObenErsteStrebe;

  const oeffnungsplatzbedarf: number =
    lichteWeite +
    LT /* Torrahmenbreite */ +
    0.1 /* Einzelpfostenbreite */ +
    0.01 /* Rahmenversatz (Torrahmen schließt nicht bündig mit Doppelpfosten ab, sondern hat 1cm Versatz) */ +
    APP.settings.SchiebetorPuffer;

  return {
    A,
    B,
    C,
    D,
    E,
    F,
    F1,
    G,
    L1,
    L2,
    L3,
    L4,
    L5,
    LT,
    rahmenBreite,
    abstandKnickObenErsteStrebe,
    oeffnungsplatzbedarf,
  };
}

export function schiebetorMasse({
  lichteWeite,
  torHöhe,
}: {
  lichteWeite: number;
  torHöhe: number;
}) {
  const { LT, D, F, rahmenBreite, abstandKnickObenErsteStrebe, oeffnungsplatzbedarf } =
    schiebetorKennzahlen({
      lichteWeite,
      torHöhe,
    });

  return {
    torBreite: LT,
    knickDeltaX: D,
    rahmenBreite,
    schrägwinkel: F,
    abstandKnickObenErsteStrebe,
    oeffnungsplatzbedarf,
  };
}

// const input = { torHöhe: 1.5, lichteWeite: 4 };
// console.log(
//   "*** TEST schiebetorMasse",
//   Debugs.roundValues({ ...input, ...schiebetorMasse(input) })
// );
// {
//     "torHöhe": 1.5,
//     "lichteWeite": 4,
//     "A": 5.98,
//     "B": 1.51,
//     "C": 4.145,
//     "D": 1.835,
//     "E": 0.19,
//     "F": 0.74655,
//     "L1": 4,
//     "L2": 1.2,
//     "L5": 0.2,
//     "L4": 1.98,
//     "LT": 5.98,
//     "L3": 1.86
// }

function createRegularNOgonShape(sides: number, radius: number, center: Vector2, rotation: number) {
  const shape = new Shape();
  const pts = [];
  // console.log({ sides, radius, center, rotation });
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides;
    const p = new Vector2(center.x + radius * Math.cos(a), center.y + radius * Math.sin(a));
    pts.push(p);
  }
  shape.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) {
    shape.lineTo(pts[i].x, pts[i].y);
  }
  shape.closePath();
  return shape;
}

export function getSchiebetorMaterial(materialName: string, materialMap: Map<string, string>) {
  materialName = materialName ?? "CHANGE_SCHIEBETOR_RAHMEN";
  const materialKey = materialMap[materialName] ?? materialName;
  const material = APP.sceneNode.getMaterial(materialKey);
  return material;
}

function extrudeShapeKante(shape: Shape, kante: Kante, material: Material) {
  const [start, end] = [kante.start, kante.end].map((k) => Maths3.v2To3xy(k.position));
  const geom = new ExtrudeGeometry(shape, {
    depth: start.distanceTo(end),
    bevelEnabled: false,
  });
  geom.computeVertexNormals();
  const lookAt = end.clone().sub(start).normalize();
  geom.lookAt(lookAt);
  const mesh = new Mesh(geom, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

const gummipufferExtruder = (
  kante: Kante,
  polygon: Polygon,
  materialName: string,
  materialMap: Map<string, string>
) => {
  const shape = createRegularNOgonShape(
    12,
    kante.breite * 0.6,
    new Vector2(-kante.breite * 0.4, -kante.breite * 0),
    0
  );
  const material = getSchiebetorMaterial(materialName, materialMap);
  return extrudeShapeKante(shape, kante, material);
};

let _rahmenschraubeModelMesh = null;

async function getRahmenschraubeModelMesh() {
  if (!_rahmenschraubeModelMesh) {
    const rahmenschraubeModel = APP.richMetadata.findModel("Schiebetor_Rahmenschraube");
    _rahmenschraubeModelMesh = await APP.modelsHelper.loadModelPromise(rahmenschraubeModel);
  }
  return _rahmenschraubeModelMesh;
}

const führungsschieneUntenExtruder = (
  kante: Kante,
  polygon: Polygon,
  materialName: string,
  materialMap: Map<string, string>
) => {
  const mm = 1 / 1000;
  const chamfer = 3;
  const lochbreite = 22;
  const breiteUnten = 53 + 2 * 3.5;
  const breiteOben = 70;
  const höhe = 60;
  const knickVonUnten = 25;
  const ps = [
    v2(-lochbreite / 2, 0),
    v2(-lochbreite / 2, -höhe / 2),
    v2(-breiteUnten / 2 + chamfer, -höhe / 2),
    v2(-breiteUnten / 2, -höhe / 2 + chamfer),
    v2(-breiteOben / 2, -höhe / 2 + knickVonUnten),
    v2(-breiteOben / 2, höhe / 2 - chamfer),
    v2(-breiteOben / 2 + chamfer, höhe / 2),
  ];
  const shape = new Shape();
  const mirror = ps.map((v) => v.clone().multiply(v2(-1, 1))).toReversed();
  [...ps, ...mirror]
    .map((p) => p.add(v2(0, höhe / 2)).multiplyScalar(mm))
    .forEach((p, index) => {
      if (index == 0) {
        shape.moveTo(p.x, p.y);
      } else {
        shape.lineTo(p.x, p.y);
      }
    });
  shape.closePath();
  const material = getSchiebetorMaterial(materialName, materialMap);
  return extrudeShapeKante(shape, kante, material);
};

export function erzeugeSchiebetorRahmen({
  torHöhe,
  lichteWeite,
  schiebetorFlügelMaxBreite,
  gespiegeltZ,
}: RahmenParams): RahmenDef {
  // console.log("###############", { gespiegeltZ });
  const {
    torBreite,
    knickDeltaX,
    rahmenBreite,
    abstandKnickObenErsteStrebe,
    oeffnungsplatzbedarf,
  } = schiebetorMasse({
    torHöhe,
    lichteWeite,
  });

  const rahmenTiefe = 0.05;

  // Eck- und Pfostenpositionen (m, aus Maßblatt grob übernommen)
  const linksX = 0;
  const rechtsX = torBreite;

  // const schrägwinkel = (-138 * Math.PI) / 180;
  // const knickDeltaX = Math.tan(schrägwinkel) * (torHöhe - rahmenBreite * 2);
  const knickObenX = torBreite - knickDeltaX;

  // FührungsschieneUnten unten
  const führungsschieneUntenBreite = 0.06;

  // Knoten anlegen (bottom y = 0, top y = hoehe_m) — x unverändert, y gespiegelt
  const linksUnten: Knoten = { position: v2(linksX, 0), gehrung: false };
  const linksOben: Knoten = { position: v2(linksX, torHöhe), gehrung: false };
  const rechtsUnten: Knoten = { position: v2(rechtsX, 0), gehrung: false };
  const knickRechts: Knoten = {
    position: v2(rechtsX, rahmenBreite + führungsschieneUntenBreite),
    gehrung: false,
  };
  const knickOben: Knoten = { position: v2(knickObenX, torHöhe), gehrung: true };

  const move = (k: Knoten, v: Vector2) => ({ ...k, position: k.position.clone().add(v) });

  const kanten: Kante[] = [];
  const dimensions: Dimension[] = [];
  const füllungsFenster: Box2[] = [];

  kanten.push({
    start: linksUnten,
    end: rechtsUnten,
    prio: 1,
    aussen: true,
    breite: führungsschieneUntenBreite,
    tiefe: 0.06,
    name: "FührungsschieneUnten",
    extruder: führungsschieneUntenExtruder,
  });

  kanten.push({
    start: { position: v2(linksX, -führungsschieneUntenBreite), gehrung: false },
    end: { position: v2(rechtsX - knickDeltaX / 2, -führungsschieneUntenBreite), gehrung: false },
    prio: 1,
    aussen: true,
    breite: führungsschieneUntenBreite,
    tiefe: 0.06,
    name: "FührungsschieneSeite",
    extruder: (
      kante: Kante,
      polygon: Polygon,
      materialName: string,
      materialMap: Map<string, string>
    ) => {
      const sz = gespiegeltZ ? -1 : 1;
      const x0 = 0.03 * sz;
      const y0 = 0.08;
      const x1 = x0 + 0.05 * sz;
      const y1 = y0 + 0.05;
      const shape = new Shape();
      shape.moveTo(x0, y0);
      shape.lineTo(x1, y0);
      shape.lineTo(x1, y1);
      shape.lineTo(x0, y1);
      shape.lineTo(x0, y0);
      shape.closePath();
      const material = getSchiebetorMaterial(materialName, materialMap);
      return extrudeShapeKante(shape, kante, material);
    },
  });
  dimensions.push({ start: linksUnten.position, end: rechtsUnten.position, offset: v2(0, 50) });
  // Aussenrahmen unten ist doppelt
  kanten.push({
    start: move(linksUnten, v2(0, führungsschieneUntenBreite)),
    end: move(rechtsUnten, v2(0, führungsschieneUntenBreite)),
    prio: 1,
    aussen: true,
    breite: rahmenBreite,
    tiefe: rahmenTiefe,
    name: "AussenrahmenUnten",
  });
  // Aussenrahmen oben
  kanten.push({
    start: knickOben,
    end: linksOben,
    prio: 1,
    aussen: true,
    breite: rahmenBreite,
    tiefe: rahmenTiefe,
    name: "AussenrahmenOben",
  });
  dimensions.push({ start: knickOben.position, end: linksOben.position, offset: v2(0, -50) });
  // Aussenrahmen schräg rechts
  kanten.push({
    start: knickRechts,
    end: knickOben,
    prio: 0,
    aussen: true,
    breite: rahmenBreite,
    tiefe: rahmenTiefe,
    name: "schrägRechts",
  });
  dimensions.push({ start: knickRechts.position, end: knickOben.position, offset: v2(0, -150) });

  dimensions.push({ start: linksUnten.position, end: linksOben.position, offset: v2(-50, 0) });

  // Streben
  // der Platz in der horizontalen für die Fächer (jedes Fach ist eine Füllung und eine Strebe)
  const platzFächer = knickObenX - abstandKnickObenErsteStrebe;
  const fächerAnzahl = Math.ceil(platzFächer / (schiebetorFlügelMaxBreite + rahmenBreite));
  // console.log("erzeugeSchiebetorRahmen", {
  //   platzFächer,
  //   rahmenBreite,
  //   fächerAnzahl,
  //   fächerPlatzMinusRahmen: platzFächer - rahmenBreite,
  //   schiebetorFlügelMaxBreitePlusRahmenBreite: schiebetorFlügelMaxBreite + rahmenBreite,
  //   knickObenX,
  //   abstandKnickObenErsteStrebe,
  // });
  if (Number.isNaN(fächerAnzahl) || fächerAnzahl <= 0) {
    throw new Error("die Konstruktion des Schiebetores ergibt kein Füllungsfenster");
  }

  const fachBreite = (platzFächer - rahmenBreite) / fächerAnzahl;
  const innerRahmenBreite = 0.5 * rahmenBreite;
  const uKlemmprofilBreite = 0.04;

  const profilMitSchraubenExtruder =
    (xOffset: number) =>
    (kante: Kante, polygon: Polygon, materialName: string, materialMap: Map<string, string>) => {
      const material = getSchiebetorMaterial(materialName, materialMap);
      const mesh = extrudePolygons([polygon], material, uKlemmprofilBreite, bevelSize);
      const schraubenMaterial = getSchiebetorMaterial("SchiebetorSchraube", materialMap);
      getRahmenschraubeModelMesh().then((schraube: Mesh) => {
        const pys = polygon.map((v) => v.y);
        const y1 = Math.min(...pys) + 0.05;
        const y2 = Math.max(...pys) - 0.05;
        const height = y2 - y1;
        const u = Maths.mapToUnitInterval(0.9, height, 1.8);
        const steps = Math.floor((1 - u) * 4 + u * 8);
        const step = height / steps;
        const x = kante.start.position.x + xOffset;
        const z = uKlemmprofilBreite / 2 + 0.002;
        const setMaterial = (obj, material) =>
          obj.traverse((o) => {
            if (o.material) o.material = material;
          });

        for (let y = y1; y <= y2 + 0.05; y += step) {
          const schraubeVorne = schraube.clone();
          const schraubeHinten = schraube.clone();
          setMaterial(schraubeVorne, schraubenMaterial);
          setMaterial(schraubeHinten, schraubenMaterial);
          schraubeVorne.position.set(x, y, z);
          schraubeHinten.position.set(x, y, -z);
          mesh.add(schraubeHinten);
          mesh.add(schraubeVorne);
        }
      });
      return mesh;
    };
  for (let i = 0; i <= fächerAnzahl; i++) {
    const x0 = i * fachBreite + rahmenBreite * 0.5;
    const x1 = (i + 1) * fachBreite + rahmenBreite * 0.5;
    // Strebe
    const bottom: Knoten = { position: v2(x0, 0), gehrung: false };
    const top: Knoten = { position: v2(x0, torHöhe), gehrung: false };
    kanten.push({
      start: bottom,
      end: top,
      prio: 0,
      breite: rahmenBreite,
      name: i == fächerAnzahl ? "StrebeRechtsVorDreieck" : "Strebe",
      extruder:
        i > 0
          ? undefined
          : (
              kante: Kante,
              polygon: Polygon,
              materialName: string,
              materialMap: Map<string, string>
            ) => {
              const material = getSchiebetorMaterial(materialName, materialMap);
              const mesh = extrudePolygons([polygon], material, rahmenTiefe, bevelSize);
              const griffmuldeModel = APP.richMetadata.findModel("Schiebetor_Griffmulde");
              APP.modelsHelper.loadModelPromise(griffmuldeModel).then(async (m: Mesh) => {
                const griffmuldeMeshVorne = m.children[0];
                const griffmuldeMeshHinten = griffmuldeMeshVorne.clone(true);
                const v = v3(rahmenBreite / 2, 0.917, rahmenTiefe / 2 + 0.001);
                griffmuldeMeshHinten.quaternion.copy(Maths3.quaternion.y180);
                griffmuldeMeshVorne.position.set(v.x, v.y, v.z);
                griffmuldeMeshHinten.position.set(v.x, v.y, -v.z);

                const toBSP = (m: Mesh) => {
                  m.updateMatrix();
                  return new ThreeBSP(new Geometry().fromBufferGeometry(m.geometry), m.matrix);
                };

                toBSP(mesh)
                  .subtract(toBSP(griffmuldeMeshVorne as Mesh))
                  .subtract(toBSP(griffmuldeMeshHinten as Mesh))
                  .setGeometryInto(mesh);
              });
              return mesh;
            },
    });
    if (i == fächerAnzahl) {
      continue;
    }
    // Innenrahmen
    const ix0 = x0 + rahmenBreite * 0.5;
    const ix1 = x1 - rahmenBreite * 0.5;
    const iy0 = rahmenBreite + führungsschieneUntenBreite;
    const iy1 = torHöhe - rahmenBreite * 1;
    const lu: Knoten = { position: v2(ix0, iy0), gehrung: false };
    const lo: Knoten = { position: v2(ix0, iy1), gehrung: false };
    const ru: Knoten = { position: v2(ix1, iy0), gehrung: false };
    const ro: Knoten = { position: v2(ix1, iy1), gehrung: false };
    // profile links/rechts
    const dx = 0.015;
    kanten.push({
      start: lo,
      end: lu,
      prio: -1,
      aussen: true,
      name: "profilVertical",
      breite: uKlemmprofilBreite,
      tiefe: uKlemmprofilBreite,
      extruder: profilMitSchraubenExtruder(dx),
    });
    kanten.push({
      start: ru,
      end: ro,
      prio: -1,
      aussen: true,
      name: "profilVertical",
      breite: uKlemmprofilBreite,
      tiefe: uKlemmprofilBreite,
      extruder: profilMitSchraubenExtruder(-dx),
    });
    // profile oben/unten
    const uprofilBreite = 0.0304;
    const uprofilTiefe = 0.0253;
    kanten.push({
      start: ro,
      end: lo,
      prio: -2,
      breite: uprofilBreite,
      tiefe: uprofilTiefe,
      aussen: true,
      name: "profilHorizontal",
    });
    kanten.push({
      start: lu,
      end: ru,
      prio: -2,
      breite: uprofilBreite,
      tiefe: uprofilTiefe,
      aussen: true,
      name: "profilHorizontal",
    });

    const fenster = new Box2(lu.position.clone(), ro.position.clone());
    füllungsFenster.push(fenster);

    dimensions.push({
      start: fenster.min.clone().setY(0),
      end: fenster.max.clone().setY(0),
      offset: v2(0, -50),
    });
  }

  // Gummipuffer links
  kanten.push({
    start: linksUnten,
    end: linksOben,
    prio: 1,
    aussen: true,
    breite: gummiPufferBreite,
    tiefe: gummiPufferBreite,
    // extruder: gummipufferExtruder,
    name: "SchiebetorGummi",
    materialName: "SchiebetorGummi",
  });

  // // kurze Strebe rechts in der Schräge
  // const kleinerPfostenX = torBreite - knickDeltaX / 2;
  // const kurzeStrebe: Kante = {
  //   start: { position: v2(kleinerPfostenX, 0), gehrung: false },
  //   end: { position: v2(kleinerPfostenX, torHöhe * 0.8), gehrung: false },
  //   prio: -1,
  //   breite: rahmenBreite,
  // };
  // kanten.push(kurzeStrebe);

  const lastRahmenEndeX = fächerAnzahl * fachBreite + rahmenBreite;
  dimensions.push({
    start: linksOben.position,
    end: v2(lastRahmenEndeX, torHöhe),
    offset: v2(0, -20),
  });
  dimensions.push({
    start: v2(lastRahmenEndeX, 0),
    end: v2(torBreite, 0),
    offset: v2(0, +30),
  });

  const knoten = [...new Set(kanten.flatMap((k) => [k.start, k.end]))];

  // gib die distanz zwischen rechtem rand der rechten strebe und dem rechten rand des tores zurück.
  const rechtesFenster = füllungsFenster.at(-1);
  const dreiecksBreite = torBreite - (rechtesFenster!.max.x + rahmenBreite + innerRahmenBreite);

  return {
    knoten,
    kanten,
    dimensions,
    füllungsFenster,
    dreiecksBreite,
    torBreite,
    lichteWeite,
    oeffnungsplatzbedarf,
  };
}
